import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Avatar,
  Image,
  IconButton,
  useToast,
  Input,
  InputGroup,
  InputRightElement,
  Collapse,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  List,
  ListItem,
  Spinner,
} from '@chakra-ui/react';
import { Heart, MessageCircle, Share2, MoreHorizontal, Send, Trash2, Flag } from 'lucide-react';
import { POKER_COLORS } from '../../theme/designSystem';
import { searchUsers } from '../../services/socialService';
import {
  Story,
  Comment,
  toggleLikeStory,
  addComment,
  deleteStory,
  formatWinAmount,
  getTimeAgo,
  getWinTier,
} from '../../services/storyService';

interface FeedCardProps {
  story: Story;
  currentUserId: string;
  currentUsername?: string;
  currentAvatarUrl?: string;
  onUserClick?: (userId: string) => void;
  onImageClick?: () => void;
  onDeleted?: () => void;
}

const FeedCard: React.FC<FeedCardProps> = ({
  story,
  currentUserId,
  currentUsername = 'Anonymous',
  currentAvatarUrl,
  onUserClick,
  onImageClick,
  onDeleted,
}) => {
  const toast = useToast();
  const [isLiking, setIsLiking] = useState(false);
  const [localLikes, setLocalLikes] = useState(story.likes || 0);
  const [localIsLiked, setLocalIsLiked] = useState(story.likedBy?.includes(currentUserId) || false);
  const [showComments, setShowComments] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [isCommenting, setIsCommenting] = useState(false);
  const [localComments, setLocalComments] = useState<Comment[]>(story.comments || []);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Reply state
  const [replyingTo, setReplyingTo] = useState<{ commentId: string; username: string } | null>(null);
  const [expandedReplies, setExpandedReplies] = useState<{ [commentId: string]: boolean }>({});
  
  // @ mention state for comments
  const [showMentions, setShowMentions] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionResults, setMentionResults] = useState<{odD: string, username: string, photoURL?: string}[]>([]);
  const [mentionLoading, setMentionLoading] = useState(false);
  const [cursorPosition, setCursorPosition] = useState(0);

  const winTier = getWinTier(story.potAmount, story.currency);
  const isOwnStory = story.odD === currentUserId;

  // Search for users when @ is typed in comment
  useEffect(() => {
    if (!mentionQuery || mentionQuery.length < 1) {
      setMentionResults([]);
      return;
    }

    const searchTimer = setTimeout(async () => {
      setMentionLoading(true);
      try {
        const results = await searchUsers(mentionQuery, 5);
        setMentionResults(results.map(u => ({
          odD: u.odwinId || u.odD || '',
          username: u.username,
          photoURL: u.photoURL,
        })));
      } catch (error) {
        console.error('Error searching users:', error);
      } finally {
        setMentionLoading(false);
      }
    }, 300);

    return () => clearTimeout(searchTimer);
  }, [mentionQuery]);

  // Handle comment input change with @ detection
  const handleCommentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const cursor = e.target.selectionStart || 0;
    setCommentText(value);
    setCursorPosition(cursor);

    // Check if we're typing after an @
    const textBeforeCursor = value.slice(0, cursor);
    const atMatch = textBeforeCursor.match(/@(\w*)$/);
    
    if (atMatch) {
      setMentionQuery(atMatch[1]);
      setShowMentions(true);
    } else {
      setShowMentions(false);
      setMentionQuery('');
    }
  };

  // Insert mention into comment
  const insertMention = (user: {username: string}) => {
    const textBeforeCursor = commentText.slice(0, cursorPosition);
    const textAfterCursor = commentText.slice(cursorPosition);
    
    // Replace the @query with @username
    const newTextBefore = textBeforeCursor.replace(/@\w*$/, `@${user.username} `);
    const newComment = newTextBefore + textAfterCursor;
    
    setCommentText(newComment);
    setShowMentions(false);
    setMentionQuery('');
  };

  const handleLike = async () => {
    if (isLiking) return;

    // Optimistic update
    setLocalIsLiked(!localIsLiked);
    setLocalLikes(prev => localIsLiked ? prev - 1 : prev + 1);

    setIsLiking(true);
    try {
      await toggleLikeStory(story.id, currentUserId);
    } catch (error) {
      // Revert on error
      setLocalIsLiked(localIsLiked);
      setLocalLikes(story.likes || 0);
      console.error('Error liking story:', error);
    } finally {
      setIsLiking(false);
    }
  };

  // Start replying to a comment
  const startReply = (commentId: string, username: string) => {
    setReplyingTo({ commentId, username });
    setCommentText(`@${username} `);
    setShowComments(true);
  };

  // Cancel reply
  const cancelReply = () => {
    setReplyingTo(null);
    setCommentText('');
  };

  const handleComment = async () => {
    if (!commentText.trim() || isCommenting) return;

    setIsCommenting(true);
    try {
      const newComment: Comment = {
        id: `${currentUserId}_${Date.now()}`,
        odD: currentUserId,
        username: currentUsername,
        avatarUrl: currentAvatarUrl,
        text: commentText.trim(),
        createdAt: new Date(),
        ...(replyingTo && { replyTo: replyingTo }),
      };
      
      await addComment(
        story.id, 
        currentUserId, 
        currentUsername, 
        currentAvatarUrl, 
        commentText.trim(),
        replyingTo || undefined
      );
      
      // Optimistic update
      setLocalComments(prev => [...prev, newComment]);
      setCommentText('');
      setReplyingTo(null);
    } catch (error) {
      console.error('Error posting comment:', error);
      toast({
        title: 'Failed to post comment',
        status: 'error',
        duration: 2000,
      });
    } finally {
      setIsCommenting(false);
    }
  };

  const handleDelete = async () => {
    if (!isOwnStory || isDeleting) return;
    
    setIsDeleting(true);
    try {
      await deleteStory(story.id, currentUserId);
      toast({
        title: 'Story deleted',
        status: 'success',
        duration: 2000,
      });
      onDeleted?.();
    } catch (error) {
      console.error('Error deleting story:', error);
      toast({
        title: 'Failed to delete',
        status: 'error',
        duration: 2000,
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleShare = async () => {
    const url = `${window.location.origin}/story/${story.id}`;
    
    // Try native share first (mobile)
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${story.username} won ${formatWinAmount(story.potAmount, story.currency)}!`,
          text: story.caption || 'Check out this win on PokerSweepStars!',
          url,
        });
        return;
      } catch (err) {
        // User cancelled or error - fall through to clipboard
        if ((err as Error).name !== 'AbortError') {
          copyToClipboard(url);
        }
      }
    } else {
      copyToClipboard(url);
    }
  };

  const copyToClipboard = (url: string) => {
    navigator.clipboard.writeText(url).then(() => {
      toast({
        title: 'Link copied!',
        status: 'success',
        duration: 2000,
      });
    }).catch(() => {
      toast({
        title: 'Could not copy link',
        status: 'error',
        duration: 2000,
      });
    });
  };

  // Render caption with clickable @mentions
  const renderCaptionWithMentions = (text: string) => {
    const parts = text.split(/(@\w+)/g);
    return parts.map((part, index) => {
      if (part.startsWith('@')) {
        const username = part.slice(1);
        return (
          <Text
            key={index}
            as="span"
            color={POKER_COLORS.cyan}
            fontWeight="semibold"
            cursor="pointer"
            _hover={{ textDecoration: 'underline' }}
            onClick={async (e) => {
              e.stopPropagation();
              // Search for user by username and navigate to profile
              try {
                const results = await searchUsers(username, 1);
                if (results.length > 0) {
                  const user = results[0];
                  const userId = user.odwinId || user.odD || '';
                  if (userId && onUserClick) {
                    onUserClick(userId);
                  }
                } else {
                  toast({
                    title: 'User not found',
                    status: 'warning',
                    duration: 2000,
                  });
                }
              } catch (error) {
                console.error('Error finding user:', error);
              }
            }}
          >
            {part}
          </Text>
        );
      }
      return part;
    });
  };

  const formatCommentTime = (createdAt: any) => {
    if (!createdAt) return '';
    const date = createdAt.toDate ? createdAt.toDate() : 
                 createdAt instanceof Date ? createdAt : new Date(createdAt);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'now';
    if (diffMins < 60) return `${diffMins}m`;
    if (diffHours < 24) return `${diffHours}h`;
    return `${diffDays}d`;
  };

  return (
    <Box
      bg={POKER_COLORS.darkBase}
      borderRadius={{ base: 0, md: 'lg' }}
      border={{ base: 'none', md: '1px solid' }}
      borderBottom={{ base: '1px solid', md: '1px solid' }}
      borderColor="whiteAlpha.100"
      overflow="hidden"
    >
      {/* Header */}
      <HStack px={3} py={2.5} spacing={3}>
        <Avatar
          size="sm"
          name={story.username}
          src={story.avatarUrl}
          bg="linear-gradient(135deg, #f97316 0%, #ea580c 100%)"
          cursor="pointer"
          onClick={() => onUserClick?.(story.odD)}
          border="2px solid"
          borderColor={isOwnStory ? POKER_COLORS.cyan : 'transparent'}
        />
        <VStack align="start" spacing={0} flex={1}>
          <HStack spacing={2}>
            <Text
              color="white"
              fontSize="sm"
              fontWeight="bold"
              cursor="pointer"
              _hover={{ color: POKER_COLORS.cyan }}
              onClick={() => onUserClick?.(story.odD)}
            >
              {story.username}
            </Text>
            {isOwnStory && (
              <Text color={POKER_COLORS.cyan} fontSize="xs">(You)</Text>
            )}
          </HStack>
          <HStack spacing={2}>
            <Text color="gray.500" fontSize="xs">
              {getTimeAgo(story.createdAt)}
            </Text>
            {story.tableName && (
              <>
                <Text color="gray.600" fontSize="xs">•</Text>
                <Text color="gray.500" fontSize="xs" isTruncated maxW="120px">
                   {story.tableName}

                </Text>
              </>
            )}
          </HStack>
        </VStack>
        
        <Menu>
          <MenuButton
            as={IconButton}
            aria-label="More options"
            icon={<MoreHorizontal size={18} />}
            variant="ghost"
            color="gray.500"
            size="sm"
            _hover={{ bg: 'whiteAlpha.100' }}
          />
          <MenuList bg="gray.900" borderColor="gray.700" minW="150px">
            <MenuItem 
              bg="gray.900" 
              _hover={{ bg: 'gray.800' }}
              onClick={handleShare}
              icon={<Share2 size={16} />}
            >
              Share
            </MenuItem>
            {isOwnStory && (
              <MenuItem 
                bg="gray.900" 
                _hover={{ bg: 'gray.800' }}
                onClick={handleDelete}
                icon={<Trash2 size={16} />}
                color="red.400"
                isDisabled={isDeleting}
              >
                Delete
              </MenuItem>
            )}
            {!isOwnStory && (
              <MenuItem 
                bg="gray.900" 
                _hover={{ bg: 'gray.800' }}
                icon={<Flag size={16} />}
                color="orange.400"
              >
                Report
              </MenuItem>
            )}
          </MenuList>
        </Menu>
      </HStack>

      {/* Image */}
      <Box
        position="relative"
        cursor={onImageClick ? 'pointer' : 'default'}
        onClick={onImageClick}
        bg="black"
      >
        <Image
          src={story.imageUrl}
          alt="Win"
          w="100%"
          maxH={{ base: '350px', md: '450px' }}
          objectFit="contain"
          loading="lazy"
        />
        
        {/* Win Badge */}
        <Box
          position="absolute"
          bottom={3}
          left={3}
          bg="blackAlpha.800"
          px={3}
          py={2}
          borderRadius="lg"
          backdropFilter="blur(10px)"
          border="1px solid"
          borderColor={winTier === 'monster' ? POKER_COLORS.gold : 'whiteAlpha.300'}
        >
          <HStack spacing={2}>
            <Text fontSize="lg"></Text>

            <VStack spacing={0} align="start">
              <Text
                fontWeight="bold"
                color={winTier === 'monster' ? POKER_COLORS.gold : winTier === 'big' ? 'purple.300' : 'white'}
                fontSize="md"
              >
                {formatWinAmount(story.potAmount, story.currency)}
              </Text>
              {story.handType && (
                <Text color="whiteAlpha.700" fontSize="xs">
                  {story.handType}
                </Text>
              )}
            </VStack>
          </HStack>
        </Box>
      </Box>

      {/* Actions */}
      <HStack px={3} py={2} spacing={4}>
        <HStack spacing={1}>
          <IconButton
            aria-label={localIsLiked ? 'Unlike' : 'Like'}
            icon={<Heart size={24} fill={localIsLiked ? '#ef4444' : 'none'} stroke={localIsLiked ? '#ef4444' : 'currentColor'} />}
            variant="ghost"
            color={localIsLiked ? 'red.400' : 'white'}
            size="sm"
            onClick={handleLike}
            isLoading={isLiking}
            _hover={{ transform: 'scale(1.1)' }}
            _active={{ transform: 'scale(0.95)' }}
          />
          <Text color="white" fontSize="sm" fontWeight="semibold">{localLikes}</Text>
        </HStack>

        <HStack spacing={1}>
          <IconButton
            aria-label="Comments"
            icon={<MessageCircle size={24} />}
            variant="ghost"
            color={showComments ? POKER_COLORS.cyan : 'white'}
            size="sm"
            onClick={() => setShowComments(!showComments)}
            _hover={{ transform: 'scale(1.1)' }}
          />
          <Text color="white" fontSize="sm" fontWeight="semibold">{localComments.length}</Text>
        </HStack>

        <Box flex={1} />

        <IconButton
          aria-label="Share"
          icon={<Share2 size={22} />}
          variant="ghost"
          color="white"
          size="sm"
          onClick={handleShare}
          _hover={{ transform: 'scale(1.1)' }}
        />
      </HStack>

      {/* Likes text */}
      {localLikes > 0 && (
        <Box px={3} pb={1}>
          <Text color="white" fontSize="sm" fontWeight="semibold">
            {localLikes} {localLikes === 1 ? 'like' : 'likes'}
          </Text>
        </Box>
      )}

      {/* Caption with @mention support */}
      {story.caption && (
        <Box px={3} pb={2}>
          <Text color="white" fontSize="sm">
            <Text as="span" fontWeight="bold" mr={2}>{story.username}</Text>
            {renderCaptionWithMentions(story.caption)}
          </Text>
        </Box>
      )}

      {/* View comments link */}
      {localComments.length > 0 && !showComments && (
        <Box px={3} pb={2}>
          <Text 
            color="gray.500" 
            fontSize="sm" 
            cursor="pointer"
            onClick={() => setShowComments(true)}
            _hover={{ color: 'gray.400' }}
          >
            View {localComments.length === 1 ? '1 comment' : `all ${localComments.length} comments`}
          </Text>
        </Box>
      )}

      {/* Comments Section */}
      <Collapse in={showComments} animateOpacity>
        <Box px={3} pb={3} borderTop="1px solid" borderColor="whiteAlpha.100" pt={3}>
          {/* Comments List */}
          <VStack align="stretch" spacing={3} maxH="200px" overflowY="auto" mb={3}
            css={{
              '&::-webkit-scrollbar': { width: '4px' },
              '&::-webkit-scrollbar-thumb': { background: '#4a5568', borderRadius: '4px' },
            }}
          >
            {localComments.length === 0 ? (
              <Text color="gray.500" fontSize="sm" textAlign="center" py={2}>
                No comments yet. Be the first!
              </Text>
            ) : (
              (() => {
                // Group comments: top-level and replies
                const topLevelComments = localComments.filter(c => !c.replyTo);
                const repliesByParent: { [key: string]: Comment[] } = {};
                
                localComments.forEach(c => {
                  if (c.replyTo) {
                    const parentId = c.replyTo.commentId;
                    if (!repliesByParent[parentId]) repliesByParent[parentId] = [];
                    repliesByParent[parentId].push(c);
                  }
                });

                return topLevelComments.map((comment, idx) => {
                  const commentId = comment.id || `${idx}`;
                  const replies = repliesByParent[commentId] || [];
                  const showReplies = expandedReplies[commentId] || false;
                  const toggleReplies = () => setExpandedReplies(prev => ({ 
                    ...prev, 
                    [commentId]: !prev[commentId] 
                  }));

                  return (
                    <Box key={commentId}>
                      {/* Main comment */}
                      <HStack spacing={2} align="start">
                        <Avatar 
                          size="xs" 
                          src={comment.avatarUrl} 
                          name={comment.username}
                          bg="linear-gradient(135deg, #f97316 0%, #ea580c 100%)"
                          cursor="pointer"
                          onClick={() => onUserClick?.(comment.odD)}
                        />
                        <Box flex={1}>
                          <Text color="white" fontSize="sm">
                            <Text 
                              as="span" 
                              fontWeight="bold"
                              cursor="pointer"
                              _hover={{ color: POKER_COLORS.cyan }}
                              onClick={() => onUserClick?.(comment.odD)}
                            >
                              {comment.username}
                            </Text>
                            {' '}{renderCaptionWithMentions(comment.text)}
                          </Text>
                          <HStack spacing={3}>
                            <Text color="gray.500" fontSize="xs">
                              {formatCommentTime(comment.createdAt)}
                            </Text>
                            <Text 
                              color="gray.500" 
                              fontSize="xs" 
                              cursor="pointer"
                              fontWeight="medium"
                              _hover={{ color: POKER_COLORS.cyan }}
                              onClick={() => startReply(commentId, comment.username)}
                            >
                              Reply
                            </Text>
                          </HStack>
                        </Box>
                      </HStack>

                      {/* Replies section */}
                      {replies.length > 0 && (
                        <Box ml={8} mt={2}>
                          {/* Show/Hide replies toggle */}
                          <Text
                            color="gray.500"
                            fontSize="xs"
                            cursor="pointer"
                            _hover={{ color: POKER_COLORS.cyan }}
                            onClick={toggleReplies}
                            mb={showReplies ? 2 : 0}
                          >
                            {showReplies 
                              ? `── Hide replies` 
                              : `── View ${replies.length} ${replies.length === 1 ? 'reply' : 'replies'}`
                            }
                          </Text>

                          {/* Collapsed replies */}
                          {showReplies && (
                            <VStack spacing={2} align="stretch">
                              {replies.map((reply, replyIdx) => (
                                <HStack key={reply.id || replyIdx} spacing={2} align="start">
                                  <Avatar 
                                    size="2xs" 
                                    src={reply.avatarUrl} 
                                    name={reply.username}
                                    bg="linear-gradient(135deg, #f97316 0%, #ea580c 100%)"
                                    cursor="pointer"
                                    onClick={() => onUserClick?.(reply.odD)}
                                  />
                                  <Box flex={1}>
                                    <Text color="white" fontSize="xs">
                                      <Text 
                                        as="span" 
                                        fontWeight="bold"
                                        cursor="pointer"
                                        _hover={{ color: POKER_COLORS.cyan }}
                                        onClick={() => onUserClick?.(reply.odD)}
                                      >
                                        {reply.username}
                                      </Text>
                                      {' '}{renderCaptionWithMentions(reply.text)}
                                    </Text>
                                    <HStack spacing={3}>
                                      <Text color="gray.600" fontSize="2xs">
                                        {formatCommentTime(reply.createdAt)}
                                      </Text>
                                      <Text 
                                        color="gray.600" 
                                        fontSize="2xs" 
                                        cursor="pointer"
                                        _hover={{ color: POKER_COLORS.cyan }}
                                        onClick={() => startReply(commentId, reply.username)}
                                      >
                                        Reply
                                      </Text>
                                    </HStack>
                                  </Box>
                                </HStack>
                              ))}
                            </VStack>
                          )}
                        </Box>
                      )}
                    </Box>
                  );
                });
              })()
            )}
          </VStack>

          {/* Comment Input with @ Mentions */}
          <Box position="relative">
            {/* Show replying to indicator */}
            {replyingTo && (
              <HStack 
                mb={2} 
                px={2} 
                py={1} 
                bg="gray.800" 
                borderRadius="md"
                justify="space-between"
              >
                <Text color="gray.400" fontSize="xs">
                  Replying to @{replyingTo.username}
                </Text>
                <Text 
                  color="gray.500" 
                  fontSize="xs" 
                  cursor="pointer"
                  _hover={{ color: 'white' }}
                  onClick={cancelReply}
                >
                  

                </Text>
              </HStack>
            )}
            <HStack spacing={2}>
              <Avatar size="xs" src={currentAvatarUrl} name={currentUsername} />
              <InputGroup size="sm">
                <Input
                  placeholder={replyingTo ? `Reply to @${replyingTo.username}...` : "Add a comment... @ to tag"}
                  value={commentText}
                  onChange={handleCommentChange}
                  onKeyPress={(e) => e.key === 'Enter' && !showMentions && handleComment()}
                  bg="whiteAlpha.100"
                  border="1px solid"
                  borderColor="whiteAlpha.200"
                  borderRadius="full"
                  color="white"
                  _placeholder={{ color: 'gray.500' }}
                  _focus={{ borderColor: POKER_COLORS.cyan, boxShadow: 'none' }}
                />
                {commentText.trim() && (
                  <InputRightElement>
                    <IconButton
                      aria-label="Post"
                      icon={<Send size={14} />}
                      size="xs"
                      variant="ghost"
                      color={POKER_COLORS.cyan}
                      onClick={handleComment}
                      isLoading={isCommenting}
                      _hover={{ bg: 'transparent' }}
                    />
                  </InputRightElement>
                )}
              </InputGroup>
            </HStack>
            
            {/* @ Mention Dropdown */}
            {showMentions && (
              <Box
                position="absolute"
                bottom="100%"
                left="28px"
                right={0}
                bg="gray.800"
                border="1px solid"
                borderColor="gray.600"
                borderRadius="md"
                maxH="150px"
                overflowY="auto"
                zIndex={20}
                mb={1}
              >
                {mentionLoading ? (
                  <HStack p={2} justify="center">
                    <Spinner size="xs" color={POKER_COLORS.cyan} />
                    <Text color="gray.400" fontSize="xs">Searching...</Text>
                  </HStack>
                ) : mentionResults.length > 0 ? (
                  <List spacing={0}>
                    {mentionResults.map((user) => (
                      <ListItem
                        key={user.odD}
                        px={3}
                        py={2}
                        cursor="pointer"
                        _hover={{ bg: 'gray.700' }}
                        onClick={() => insertMention(user)}
                      >
                        <HStack>
                          <Avatar 
                            size="xs" 
                            name={user.username} 
                            src={user.photoURL}
                            bg="linear-gradient(135deg, #f97316 0%, #ea580c 100%)"
                          />
                          <Text color="white" fontSize="sm">@{user.username}</Text>
                        </HStack>
                      </ListItem>
                    ))}
                  </List>
                ) : mentionQuery ? (
                  <Text color="gray.500" fontSize="xs" p={2}>
                    No users found
                  </Text>
                ) : null}
              </Box>
            )}
          </Box>
        </Box>
      </Collapse>

      {/* Views */}
      <Box px={3} pb={3}>
        <Text color="gray.600" fontSize="xs">
          {story.views || 0} views
        </Text>
      </Box>
    </Box>
  );
};

export default FeedCard;